import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { firestore } from '../firebase';
import { useAuth } from './AuthContext';
import { hasFeature as checkFeature, resolveUserFeatures } from '../utils/features';

const FeaturesContext = createContext(null);

export function useFeatures() {
  const context = useContext(FeaturesContext);
  if (!context) {
    throw new Error('useFeatures must be used within a FeaturesProvider');
  }
  return context;
}

export function FeaturesProvider({ children }) {
  const { user, userId, loading: authLoading } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return undefined;

    if (!userId) {
      setUserData(null);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const q = query(collection(firestore, 'users'), where('uid', '==', userId));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setUserData(snap.empty ? null : snap.docs[0].data());
        setLoading(false);
      },
      () => setLoading(false)
    );

    return () => unsub();
  }, [userId, authLoading]);

  const features = useMemo(
    () => resolveUserFeatures(userData, user?.email),
    [userData, user?.email]
  );

  const hasFeature = useMemo(
    () => (feature) => checkFeature(features, feature),
    [features]
  );

  const value = useMemo(
    () => ({
      features,
      hasFeature,
      loading: authLoading || loading,
      isBeta: features.length >= 6,
    }),
    [features, hasFeature, authLoading, loading]
  );

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
}
